import type { VariableType } from '../types/block'

/** The symbologies the Phase-1 pipeline actually renders. */
export const RENDERABLE_SYMBOLOGIES = [
  'code-128',
  'ean-13',
  'qr',
  'gs1-128',
  'itf',
] as const
export type RenderableSymbology = (typeof RENDERABLE_SYMBOLOGIES)[number]

/**
 * Quiet-zone requirement in MODULE WIDTHS on each side of the symbol.
 * Linear symbologies need horizontal quiet zones only (top/bottom = 0);
 * QR needs 4 modules on ALL sides (ISO/IEC 18004).
 */
export interface QuietZoneModules {
  left: number
  right: number
  top: number
  bottom: number
}

/**
 * Per-symbology physical size facts — the ONE source of truth for barcode
 * default/min X-dimension, default bar height, quiet zones and the
 * human-readable-line default. `barcodeIntrinsicSize` (canvas size), the
 * plan builders (ZPL module/height dots) and the designer Size property all
 * read from here so they cannot drift.
 *
 * `minModuleWidthMm` is a conservative scannability floor (EAN-13 uses the
 * GS1 80% magnification minimum; the linear floor is ~7.5 mil). Tunable.
 *
 * `quietZoneModules` per GS1 General Specifications: code-128/gs1-128 = 10
 * modules each side; EAN-13 = 11 left / 7 right; QR = 4 modules all sides.
 */
export interface SymbologyMetrics {
  /** Default X-dimension (narrowest module) in mm. */
  defaultModuleWidthMm: number
  /** Minimum scannable X-dimension in mm — enforced as a hard floor in the UI. */
  minModuleWidthMm: number
  /** Default bar height in mm (1D). Square symbologies derive height from width. */
  defaultHeightMm: number
  /** Module count when width is data-independent (EAN-13 = 95). */
  fixedModuleCount?: number
  /** Quiet zone in module widths per side — part of the symbol's intrinsic box. */
  quietZoneModules: QuietZoneModules
  /** What `humanReadable: undefined` means for this symbology (pipeline + UI). */
  defaultHumanReadable: boolean
}

const LINEAR_QUIET_128: QuietZoneModules = { left: 10, right: 10, top: 0, bottom: 0 }

export const SYMBOLOGY_METRICS: Record<RenderableSymbology, SymbologyMetrics> = {
  'code-128': {
    defaultModuleWidthMm: 0.25, minModuleWidthMm: 0.19, defaultHeightMm: 12,
    quietZoneModules: LINEAR_QUIET_128, defaultHumanReadable: false,
  },
  'gs1-128': {
    defaultModuleWidthMm: 0.25, minModuleWidthMm: 0.19, defaultHeightMm: 12,
    quietZoneModules: LINEAR_QUIET_128, defaultHumanReadable: true,
  },
  'ean-13': {
    defaultModuleWidthMm: 0.33, minModuleWidthMm: 0.264, defaultHeightMm: 12, fixedModuleCount: 95,
    quietZoneModules: { left: 11, right: 7, top: 0, bottom: 0 }, defaultHumanReadable: true,
  },
  qr: {
    defaultModuleWidthMm: 0.5, minModuleWidthMm: 0.25, defaultHeightMm: 20,
    quietZoneModules: { left: 4, right: 4, top: 4, bottom: 4 }, defaultHumanReadable: false,
  },
  /**
   * ITF-14 (Interleaved 2-of-5, GS1's GTIN-14 carrier for cartons/logistics
   * units — issue #129). GS1 General Specifications / GS1 UK's ITF-14 sizing
   * guide: compliant X-dimension range is 0.495mm–1.016mm, restricted to
   * <0.635mm for LABEL printing (vs. direct-to-corrugated); we default to the
   * label-compliant floor. Bar height is NOT freely tunable per spec — GS1
   * fixes it at 31.75mm regardless of magnification (only width scales).
   *
   * `fixedModuleCount` derivation: ITF is a 2-of-5 code — EVERY digit 0-9 is
   * encoded as exactly 2 wide + 3 narrow elements among 5, so a digit's
   * contribution is constant regardless of its value. For n=14 digits (7
   * interleaved pairs), with wide:narrow ratio r:
   *   start (N,N,N,N) = 4 modules
   *   7 pairs × 2 elements-sets(bars+spaces) × (2r+3) = 14×(2r+3)
   *   stop (W,N,N) = r+2 modules
   *   total = 4 + 14(2r+3) + (r+2) = 48 + 29r
   * GS1 requires r ∈ [2.25, 3.0]; we assume the 2.5 midpoint (no ^BY ratio
   * param is ever transmitted — see serialize.ts — so this only drives the
   * CANVAS size estimate, never the literal ZPL bytes). 48+29×2.5 = 120.5.
   * Pending a live-printer probe to confirm the firmware's actual default
   * ratio (see recon notes on render-129-130).
   */
  itf: {
    defaultModuleWidthMm: 0.495, minModuleWidthMm: 0.495, defaultHeightMm: 31.75,
    fixedModuleCount: 120.5,
    quietZoneModules: LINEAR_QUIET_128, defaultHumanReadable: true,
  },
}

/**
 * Default size (pt) of the pipeline-emitted human-readable line under
 * code-128 / gs1-128 bars. Probe-derived (2026-06-11, Neodynamic at 203dpi):
 * the NATIVE ^BC interpretation line renders as a ^A0-like font of
 * ~10 × module-width dots — ≈ 2.5mm ≈ 7.1pt at the default 0.25mm module —
 * and scales up with `^BY` (the "far too big" complaint). We default to a
 * fixed 7pt: near-native at the default module width, deliberately NOT
 * scaling with bar width. Override per component via `humanReadableSizePt`.
 */
export const DEFAULT_HRI_SIZE_PT = 7

/** Canonical SymbolDataStructure.kind for each renderable symbology. */
export function canonicalStructureKind(
  symbology: RenderableSymbology
): 'raw' | 'gtin' | 'gs1-ai' {
  switch (symbology) {
    case 'code-128':
    case 'qr':
      return 'raw'
    case 'ean-13':
    case 'itf':
      return 'gtin'
    case 'gs1-128':
      return 'gs1-ai'
    default: {
      const _exhaustive: never = symbology
      throw new Error(`canonicalStructureKind: unhandled symbology ${String(_exhaustive)}`)
    }
  }
}

export interface Gs1AiInfo {
  ai: string
  fixedLength: boolean
  checkDigit: 'gtin' | 'sscc' | 'none'
  defaultVarType: VariableType
}

/**
 * Common GS1 Application Identifiers. Structural facts only — UI labels and
 * suggested variable names live designer-side (see symbol-config.ts).
 */
export const GS1_AI_DICTIONARY: Record<string, Gs1AiInfo> = {
  '00': { ai: '00', fixedLength: true, checkDigit: 'sscc', defaultVarType: 'text' }, // SSCC
  '01': { ai: '01', fixedLength: true, checkDigit: 'gtin', defaultVarType: 'text' }, // GTIN
  '10': { ai: '10', fixedLength: false, checkDigit: 'none', defaultVarType: 'text' }, // batch/lot
  '11': { ai: '11', fixedLength: true, checkDigit: 'none', defaultVarType: 'date' }, // production
  '17': { ai: '17', fixedLength: true, checkDigit: 'none', defaultVarType: 'date' }, // use-by
  '21': { ai: '21', fixedLength: false, checkDigit: 'none', defaultVarType: 'text' }, // serial
  '30': { ai: '30', fixedLength: false, checkDigit: 'none', defaultVarType: 'number' }, // count
  '3103': { ai: '3103', fixedLength: true, checkDigit: 'none', defaultVarType: 'number' }, // net kg
}

/**
 * GS1 predefined (fixed) length AIs are those whose first two digits are in
 * this set (GS1 General Specifications, "predefined-length" table). Everything
 * else — including custom/unknown AIs — is variable-length and therefore needs
 * an FNC1 separator when another element follows.
 */
const FIXED_LENGTH_PREFIXES = new Set([
  '00', '01', '02', '03', '04',
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
  '31', '32', '33', '34', '35', '36',
  '41',
])

export function isFixedLengthAi(ai: string): boolean {
  return FIXED_LENGTH_PREFIXES.has(ai.slice(0, 2))
}
